import { ActionButton } from '@/components/ActionButton';
import { getLibraryQuizV2 } from '@/features/Quiz/api/libraryQuizDetailV2';
import {
    getLibraryQuizSyncStatusV2,
    mergeLocaleStatuses,
    quizSyncErrorTitle,
    quizSyncToastTitle,
    syncLibraryQuizV2,
} from '@/features/Quiz/api/syncLibraryQuizV2';
import type {
    LocaleSyncStatus,
    QuizSyncStatusCode,
} from '@/features/Quiz/api/syncLibraryQuizV2';
import { useNotificationStore } from '@/stores/notifications';
import {
    Badge,
    Box,
    Button,
    Flex,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
} from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import React, { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

const STATUS_STYLE: Record<
    QuizSyncStatusCode,
    { label: string; bg: string; color: string }
> = {
    IN_SYNC: { label: 'In sync', bg: '#E8F5E9', color: '#1B7A2E' },
    OUT_OF_DATE: { label: 'Needs sync', bg: '#FFF4E0', color: '#B36B00' },
    PENDING: { label: 'Pending', bg: '#E7F1FF', color: '#1A6FD1' },
    PARTIAL: { label: 'Partial', bg: '#FFF4E0', color: '#B36B00' },
    FAILED: { label: 'Failed', bg: '#FDECEC', color: '#B00020' },
    SOFT_DELETED: { label: 'Deleted', bg: '#EFEFEF', color: '#777' },
    NOT_SYNCED: { label: 'Not synced', bg: '#EFEFEF', color: '#777' },
};

const StatusBadge = ({
    status,
    syncing,
    override,
}: {
    status: QuizSyncStatusCode;
    syncing?: boolean;
    override?: { label: string; bg: string; color: string };
}) => {
    const style = syncing
        ? { label: 'Syncing', bg: '#E7F1FF', color: '#1A6FD1' }
        : override ?? STATUS_STYLE[status] ?? STATUS_STYLE.NOT_SYNCED;
    return (
        <Badge
            px="10px"
            py="2px"
            borderRadius="12px"
            fontWeight="500"
            fontSize="12px"
            textTransform="none"
            bg={style.bg}
            color={style.color}>
            {style.label}
        </Badge>
    );
};

const languages = (count: number): string =>
    count === 1 ? '1 language' : `${count} languages`;

const syncSubtitle = (
    inFlight: boolean,
    outOfDateCount: number,
    notSyncedCount: number,
    lastSynced?: string
): string => {
    if (inFlight) return 'Sync in progress';
    if (notSyncedCount > 0 && outOfDateCount > 0) {
        return `${languages(notSyncedCount)} never translated, ${outOfDateCount} behind the English version`;
    }
    if (notSyncedCount > 0) {
        return notSyncedCount === 1
            ? '1 language has never been translated'
            : `${notSyncedCount} languages have never been translated`;
    }
    if (outOfDateCount > 0) {
        return outOfDateCount === 1
            ? '1 language is behind the English version'
            : `${outOfDateCount} languages are behind the English version`;
    }
    if (lastSynced) return `Last synced ${formatSyncedAt(lastSynced)}`;
    return 'All translations are up to date';
};

const formatSyncedAt = (value: string | null): string => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return formatDistanceToNow(date, { addSuffix: true });
};

const englishQuestionCount = (sections?: { items?: unknown[] }[]): number =>
    (sections ?? []).reduce((sum, section) => sum + (section.items?.length ?? 0), 0);

const questions = (count: number): string =>
    count === 1 ? '1 question' : `${count} questions`;

const manualSummary = (manualCount: number, reviewCount: number): string => {
    const parts = [];
    if (reviewCount > 0) {
        parts.push(
            `${questions(reviewCount)} ${reviewCount === 1 ? 'needs' : 'need'} review before sync overwrites manual edits`
        );
    }
    if (manualCount > 0) {
        parts.push(`${questions(manualCount)} were manually edited and sync will skip them`);
    }
    return parts.join('. ');
};

const manualStatusOverride = (
    row: LocaleSyncStatus
): { label: string; bg: string; color: string } | undefined => {
    if (row.needsReview) {
        return { label: 'Needs review', bg: '#FFF4E0', color: '#B36B00' };
    }
    if (row.manual) {
        return { label: 'Manual edits', bg: '#EAF3FF', color: '#1A6FD1' };
    }
    return undefined;
};

export const QuizSyncStatusPage = () => {
    const { quizUuid = '' } = useParams<{ quizUuid: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { addNotification } = useNotificationStore();

    const backTo =
        (location.state as { from?: string } | null)?.from ??
        (location.pathname.includes('/quiz/library') ? '/quiz/library' : '/quiz');

    const statusQuery = useQuery(
        ['library-quiz-sync-status', quizUuid],
        () => getLibraryQuizSyncStatusV2(quizUuid),
        {
            enabled: !!quizUuid,
            refetchInterval: (data) => (data?.syncInFlight ? 3000 : false),
        }
    );

    const quizQuery = useQuery(
        ['library-quiz-v2', quizUuid],
        () => getLibraryQuizV2(quizUuid),
        { enabled: !!quizUuid }
    );

    const locales = useMemo(
        () => mergeLocaleStatuses(statusQuery.data?.locales),
        [statusQuery.data?.locales]
    );

    const failedLocales = locales.filter((row) => row.status === 'FAILED').map((row) => row.locale);
    const outOfDateCount = locales.filter((row) => row.status === 'OUT_OF_DATE').length;
    const notSyncedCount = locales.filter((row) => row.status === 'NOT_SYNCED').length;
    const manualQuestionCount = locales.reduce((sum, row) => sum + row.manual, 0);
    const reviewQuestionCount = locales.reduce((sum, row) => sum + row.needsReview, 0);

    const syncMutation = useMutation(
        (targetLocales?: string[]) => syncLibraryQuizV2(quizUuid, targetLocales),
        {
            onSuccess: (accepted) => {
                addNotification({
                    type: 'info',
                    title: quizSyncToastTitle(accepted),
                });
                queryClient.invalidateQueries(['library-quiz-sync-status', quizUuid]);
            },
            onError: (error: unknown) => {
                addNotification({ type: 'error', title: quizSyncErrorTitle(error) });
            },
        }
    );

    const title = quizQuery.data?.quiz.title ?? 'Quiz translation sync';
    const sourceQuestionCount = englishQuestionCount(quizQuery.data?.sections);
    const syncedAtValues = locales
        .map((row) => row.lastAppliedAt)
        .filter((value): value is string => !!value)
        .sort();
    const lastSynced = syncedAtValues[syncedAtValues.length - 1];

    if (statusQuery.isLoading || quizQuery.isLoading) {
        return (
            <Flex justify="center" py="80px">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (statusQuery.isError) {
        return (
            <Box>
                <Text color="#cc0000" mb={4}>
                    {(statusQuery.error as { message?: string })?.message ??
                        'Could not load translation sync status for this quiz.'}
                </Text>
                <Button onClick={() => navigate(backTo)} variant="ghost">
                    Back to quizzes
                </Button>
            </Box>
        );
    }

    return (
        <Box pb="80px">
            <Text
                as={Link}
                to={backTo}
                color="#4193F4"
                fontSize="14px"
                display="inline-block"
                mb={4}>
                ‹ Back to quizzes
            </Text>

            <Flex justify="space-between" align="flex-start" gap={4} mb={6} wrap="wrap">
                <Box>
                    <Flex align="center" gap={3} wrap="wrap">
                        <Text as="h1" fontSize="24px" fontWeight="600" m={0}>
                            {title}
                        </Text>
                        <Badge
                            variant="outline"
                            colorScheme="blue"
                            borderRadius="12px"
                            px="10px"
                            py="2px"
                            textTransform="none">
                            English source
                        </Badge>
                    </Flex>
                    <Text fontSize="12px" color="#777" mt={1}>
                        {syncSubtitle(
                            !!statusQuery.data?.syncInFlight,
                            outOfDateCount,
                            notSyncedCount,
                            lastSynced
                        )}
                    </Text>
                </Box>
                <Button
                    variant="brand"
                    isLoading={syncMutation.isLoading}
                    onClick={() => syncMutation.mutate(undefined)}>
                    Sync all languages
                </Button>
            </Flex>

            {(manualQuestionCount > 0 || reviewQuestionCount > 0) && (
                <Box
                    bg={reviewQuestionCount > 0 ? '#FFF4E0' : '#EAF3FF'}
                    border="1px solid #E5EBF3"
                    borderRadius="8px"
                    p={4}
                    mb={4}>
                    <Text fontWeight="600" mb={1}>
                        Manual translation edits detected
                    </Text>
                    <Text fontSize="13px" color="#555">
                        {manualSummary(manualQuestionCount, reviewQuestionCount)}
                    </Text>
                </Box>
            )}

            <Table bg="#fff" border="1px solid #E5EBF3">
                <Thead>
                    <Tr>
                        <Th textAlign="left">Language</Th>
                        <Th textAlign="left">Questions</Th>
                        <Th textAlign="left">Status</Th>
                        <Th textAlign="left">Manual</Th>
                        <Th textAlign="left">Review</Th>
                        <Th textAlign="left">Last synced</Th>
                        <Th textAlign="left">Actions</Th>
                    </Tr>
                </Thead>
                <Tbody>
                    <Tr bg="#EAF3FF">
                        <Td>English</Td>
                        <Td>{sourceQuestionCount || '—'}</Td>
                        <Td>Source</Td>
                        <Td>—</Td>
                        <Td>—</Td>
                        <Td>{formatSyncedAt(lastSynced ?? null)}</Td>
                        <Td>
                            <Text as={Link} to={`/quiz/library/edit/${quizUuid}`} color="#4193F4">
                                Edit source
                            </Text>
                        </Td>
                    </Tr>
                    {locales.map((row) => (
                        <LocaleRow
                            key={row.locale}
                            row={row}
                            syncing={!!statusQuery.data?.syncInFlight && row.status === 'PENDING'}
                            isSyncingThis={
                                syncMutation.isLoading &&
                                !!syncMutation.variables &&
                                syncMutation.variables.length === 1 &&
                                syncMutation.variables[0] === row.locale
                            }
                            onSync={() => syncMutation.mutate([row.locale])}
                        />
                    ))}
                </Tbody>
            </Table>

            <Flex
                position="sticky"
                bottom={0}
                bg="#fff"
                borderTop="1px solid #E5EBF3"
                py={3}
                mt={6}
                justify="flex-end"
                gap={3}>
                <Button
                    variant="ghost"
                    isDisabled={failedLocales.length === 0 || syncMutation.isLoading}
                    onClick={() => syncMutation.mutate(failedLocales)}>
                    Retry failed
                </Button>
                <Button
                    variant="brand"
                    isLoading={syncMutation.isLoading && !syncMutation.variables}
                    onClick={() => syncMutation.mutate(undefined)}>
                    Sync all languages
                </Button>
            </Flex>
        </Box>
    );
};

const LocaleRow = ({
    row,
    syncing,
    isSyncingThis,
    onSync,
}: {
    row: LocaleSyncStatus;
    syncing: boolean;
    isSyncingThis: boolean;
    onSync: () => void;
}) => (
    <Tr>
        <Td>{row.displayName || row.locale}</Td>
        <Td>{row.questionCount || '—'}</Td>
        <Td>
            <StatusBadge
                status={row.status}
                syncing={syncing}
                override={manualStatusOverride(row)}
            />
        </Td>
        <Td>{row.manual ? `${row.manual} manual` : '—'}</Td>
        <Td>{row.needsReview ? `${row.needsReview} review` : '—'}</Td>
        <Td>{formatSyncedAt(row.lastAppliedAt)}</Td>
        <Td>
            <Flex align="center" gap={2}>
                <ActionButton
                    tooltipTitle="Sync this language"
                    icon="arrows-rotate"
                    isInProcess={false}
                    isLoading={isSyncingThis}
                    handleClick={onSync}
                />
            </Flex>
        </Td>
    </Tr>
);

export default QuizSyncStatusPage;
